import { __read } from "tslib";
import { FontIcon } from '@fluentui/react';
import { MenuItemTypes } from '@office-start/common-types';
import { IconNames } from '@office-start/fabric-icon-subset-utility';
import { getKey, KeyboardKey } from '@office-start/keystroke-handler-utility';
import classNames from 'classnames';
import React, { forwardRef, useCallback, useEffect, useRef, useState, } from 'react';
import styles from './DropdownMenu.module.scss';
import { usePrevious } from './hooks/usePrevious';
import { MenuSize } from './models/MenuSize';
var supportedKeys = new Set([
    KeyboardKey.ArrowDown,
    KeyboardKey.ArrowUp,
    KeyboardKey.End,
    KeyboardKey.Enter,
    KeyboardKey.Escape,
    KeyboardKey.Home,
    KeyboardKey.Tab,
]);
export var DropdownMenu = forwardRef(function (_a, ref) {
    var _b, _c, _d;
    var _e;
    var buttonContent = _a.buttonContent, items = _a.items, dropdownMenuContent = _a.dropdownMenuContent, options = _a.options, menuItemType = _a.menuItemType, onKeySelected = _a.onKeySelected, onDropdownOpened = _a.onDropdownOpened, onDropdownClosed = _a.onDropdownClosed, defaultSelected = _a.defaultSelected, stopButtonEventPropagation = _a.stopButtonEventPropagation, checkedItemClassName = _a.checkedItemClassName, buttonTitle = _a.buttonTitle, buttonAriaLabel = _a.buttonAriaLabel, disableFocus = _a.disableFocus, ariaDescribedby = _a.ariaDescribedby;
    var menuType = menuItemType;
    var _f = __read(useState(false), 2), opened = _f[0], setOpened = _f[1];
    var previousOpened = usePrevious(opened);
    var _g = __read(useState(typeof defaultSelected === 'string'
        ? [defaultSelected]
        : defaultSelected
            ? defaultSelected
            : ['']), 2), selectedKeys = _g[0], setSelectedKeys = _g[1];
    var componentRef = useRef(null);
    var menuItemRefs = useRef([]);
    var buttonRef = useRef(null);
    useEffect(function () {
        var listener = function (e) {
            // Note: when using controls that change on click, add pointer-events: none to its class
            // Otherwise dropdown will close unexpectedly on click since the target disappears from the DOM
            if (componentRef.current &&
                e.target instanceof Node &&
                componentRef.current.contains(e.target)) {
                return;
            }
            setOpened(false);
        };
        document.addEventListener('click', listener);
        return function () {
            document.removeEventListener('click', listener);
        };
    }, []);
    useEffect(function () {
        var keyListener = function (e) {
            var _a;
            if (componentRef.current &&
                e.target instanceof Node &&
                componentRef.current.contains(e.target) &&
                e.key === KeyboardKey.Escape) {
                setOpened(false);
                (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        };
        document.addEventListener('keydown', keyListener);
        return function () {
            document.removeEventListener('keydown', keyListener);
        };
    }, []);
    useEffect(function () {
        var _a, _b;
        if (opened) {
            if (onDropdownOpened) {
                onDropdownOpened();
            }
            (_a = menuItemRefs.current[0]) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else if (onDropdownClosed && previousOpened) {
            onDropdownClosed();
            (_b = buttonRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }, [onDropdownOpened, onDropdownClosed, opened, previousOpened]);
    useEffect(function () {
        if (defaultSelected && typeof defaultSelected === 'string') {
            setSelectedKeys([defaultSelected]);
        }
        if (defaultSelected && typeof defaultSelected !== 'string') {
            setSelectedKeys(defaultSelected);
        }
    }, [defaultSelected]);
    var moveMenuFocus = useCallback(function (move) {
        if (!opened) {
            return;
        }
        var limit = menuItemRefs.current.length;
        menuItemRefs.current.some(function (ref, index) {
            var _a;
            if (ref && ref === document.activeElement) {
                (_a = menuItemRefs.current[(index + move + limit) % limit]) === null || _a === void 0 ? void 0 : _a.focus();
                return true;
            }
            return false;
        });
    }, [opened]);
    var propOptions = options ? options : {};
    var menuSize = (_e = propOptions.menuSize) !== null && _e !== void 0 ? _e : MenuSize.Default;
    var handleDropdownItemClick = useCallback(function (item, e) {
        var isCurrentlySelected = selectedKeys.indexOf(item.key) >= 0;
        // Flip the currentlySelected flag and fire the callbacks
        onKeySelected &&
            onKeySelected(item.key, e, !isCurrentlySelected);
        if (!propOptions.multiSelect) {
            setSelectedKeys([item.key]);
            setOpened(false);
            return;
        }
        else {
            setSelectedKeys(isCurrentlySelected
                ? selectedKeys.filter(function (key) { return key !== item.key; })
                : selectedKeys.concat(item.key));
        }
    }, [onKeySelected, propOptions.multiSelect, selectedKeys]);
    var handleMenuItemKeyDown = useCallback(function (e, item) {
        var _a, _b, _c, _d;
        var key = getKey(e);
        if (!supportedKeys.has(key)) {
            return;
        }
        e.preventDefault();
        switch (key) {
            case KeyboardKey.Enter:
                {
                    handleDropdownItemClick(item, e);
                }
                break;
            case KeyboardKey.Escape:
                setOpened(false);
                if (typeof ref !== 'function') {
                    (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.focus();
                }
                (_b = buttonRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                if (stopButtonEventPropagation) {
                    e.stopPropagation();
                }
                break;
            case KeyboardKey.ArrowUp:
                moveMenuFocus(-1);
                break;
            case KeyboardKey.ArrowDown:
                moveMenuFocus(1);
                break;
            case KeyboardKey.End:
                (_c = menuItemRefs.current[menuItemRefs.current.length - 1]) === null || _c === void 0 ? void 0 : _c.focus();
                break;
            case KeyboardKey.Home:
                (_d = menuItemRefs.current[0]) === null || _d === void 0 ? void 0 : _d.focus();
                break;
            case KeyboardKey.Tab:
                moveMenuFocus(e.shiftKey ? -1 : 1);
                break;
            default:
                break;
        }
    }, [
        handleDropdownItemClick,
        moveMenuFocus,
        ref,
        stopButtonEventPropagation,
    ]);
    return (React.createElement("div", { ref: componentRef, className: classNames(styles['menu-wrapper'], (_b = {},
            _b[styles['menu-wrapper--default']] = menuSize !== MenuSize.Small,
            _b)) },
        React.createElement("button", { ref: ref !== null && ref !== void 0 ? ref : buttonRef, className: classNames(styles.button, (_c = {},
                _c[styles['button--default']] = menuSize !== MenuSize.Small,
                _c[styles['button--small']] = menuSize === MenuSize.Small,
                _c[styles['button--hover']] = !propOptions.disableHoverColor,
                _c)), onClick: function (e) {
                e.preventDefault();
                if (stopButtonEventPropagation) {
                    e.stopPropagation();
                }
                setOpened(!opened);
            }, onKeyDown: function (e) {
                var key = getKey(e);
                if (key === KeyboardKey.Enter ||
                    key === KeyboardKey.Space ||
                    key === KeyboardKey.Escape) {
                    e.preventDefault();
                    if (stopButtonEventPropagation) {
                        e.stopPropagation();
                    }
                    key === KeyboardKey.Escape
                        ? setOpened(false)
                        : setOpened(!opened);
                }
            }, title: buttonTitle ? buttonTitle : '', "aria-label": buttonAriaLabel ? buttonAriaLabel : '', "aria-expanded": opened, "data-clicklog": "custom", tabIndex: disableFocus ? -1 : 0, "aria-describedby": ariaDescribedby }, buttonContent),
        React.createElement("div", { className: classNames(styles.menu, (_d = {},
                _d[styles.hidden] = !opened,
                _d[styles['menu--right']] = !propOptions.leftAligned,
                _d[styles['menu--large']] = menuSize === MenuSize.Large,
                _d)) }, dropdownMenuContent ? (dropdownMenuContent) : (React.createElement("ul", { className: classNames(styles['menu-content']), role: menuType === MenuItemTypes.Radio
                ? 'radiogroup'
                : 'menu' }, items &&
            items.map(function (item, index) {
                var _a, _b, _c, _d;
                return (React.createElement("li", { onClick: function (e) {
                        handleDropdownItemClick(item, e);
                    }, onKeyDown: function (e) {
                        return handleMenuItemKeyDown(e, item);
                    }, key: item.key, className: classNames(styles['menu-item-wrapper'], (_a = {},
                        _a[styles['menu-item-wrapper--default']] = menuSize !== MenuSize.Small,
                        _a[styles['menu-item-wrapper--small']] = menuSize === MenuSize.Small,
                        _a[styles['upper-divider']] = item.hasUpperDivider,
                        _a)), ref: function (el) {
                        return (menuItemRefs.current[index] = el);
                    }, tabIndex: -1, role: menuType === MenuItemTypes.Radio
                        ? 'radio'
                        : menuItemType ===
                            MenuItemTypes.Checkbox
                            ? 'menuitemcheckbox'
                            : 'menuitem', "aria-checked": menuType === MenuItemTypes.Radio ||
                        menuType === MenuItemTypes.Checkbox
                        ? selectedKeys.indexOf(item.key) > -1 &&
                            !checkedItemClassName
                        : undefined, "aria-label": item.ariaLabel },
                    React.createElement("div", { className: classNames(styles['menu-item']) },
                        menuType ===
                            MenuItemTypes.Radio && (React.createElement("span", { className: classNames(styles['radio'], (_b = {},
                                _b[styles['radio--unchecked']] = selectedKeys.indexOf(item.key) <= -1,
                                _b["".concat(checkedItemClassName)] = selectedKeys.indexOf(item.key) > -1 &&
                                    checkedItemClassName,
                                _b[styles['menu-option-background']] = selectedKeys.indexOf(item.key) > -1 &&
                                    !checkedItemClassName,
                                _b)) },
                            React.createElement("span", { className: classNames(styles['radio-circle'], (_c = {},
                                    _c[styles['radio-circle--active']] = selectedKeys.indexOf(item.key) > -1 &&
                                        !checkedItemClassName,
                                    _c)) }))),
                        menuType ===
                            MenuItemTypes.Checkbox && (React.createElement("span", { className: classNames(styles['checkbox'], (_d = {},
                                _d[styles['checkbox--unchecked']] = selectedKeys.indexOf(item.key) <= -1,
                                _d["".concat(checkedItemClassName)] = selectedKeys.indexOf(item.key) > -1 &&
                                    checkedItemClassName,
                                _d[styles['menu-option-background']] = selectedKeys.indexOf(item.key) > -1,
                                _d)) },
                            React.createElement(FontIcon, { className: styles['checkmark'], iconName: IconNames.CheckMark }))),
                        item.iconElement ? (React.createElement("div", { className: classNames(styles['icon']) }, item.iconElement)) : null,
                        React.createElement("div", { className: classNames(styles['menu-item-text']) }, item.content))));
            }))))));
});
//# sourceMappingURL=DropdownMenu.js.map